import type { Identifier } from "ra-core";
import { useGetList } from "ra-core";

export interface ReferralCode {
  id: number;
  code: string;
  contact_id: number | null;
  company_id: number | null;
  agreement_id: number | null;
  active: boolean;
}

const params = {
  pagination: { page: 1, perPage: 50 },
  sort: { field: "code", order: "ASC" as const },
};

// Active Shopify discount codes of a person, or of an agreement (its own
// codes and those of its person), each with a button to copy it (task 1.11)
export const useReferralCodes = ({
  contactId,
  agreementId,
}: {
  contactId?: Identifier | null;
  agreementId?: Identifier | null;
}) => {
  const { data: byContact = [] } = useGetList<ReferralCode>(
    "referral_codes",
    { ...params, filter: { contact_id: contactId, active: true } },
    { enabled: contactId != null },
  );
  const { data: byAgreement = [] } = useGetList<ReferralCode>(
    "referral_codes",
    { ...params, filter: { agreement_id: agreementId, active: true } },
    { enabled: agreementId != null },
  );
  const seen = new Set<number>();
  return [...byAgreement, ...byContact]
    .filter((code) => !seen.has(code.id) && seen.add(code.id))
    .sort((a, b) => a.code.localeCompare(b.code));
};

// Active codes linked to the agreements of a list, by agreement
export const useAgreementCodes = (ids: Identifier[]) => {
  const { data = [] } = useGetList<ReferralCode>(
    "referral_codes",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "code", order: "ASC" },
      filter: { "agreement_id@in": `(${ids.join(",")})`, active: true },
    },
    { enabled: ids.length > 0 },
  );
  return (id: Identifier) =>
    data.filter((code) => String(code.agreement_id) === String(id));
};
